import PropTypes from 'prop-types';
import { action, extendObservable, reaction } from 'mobx';
import { PropTypes as MobxPropTypes } from 'mobx-react';
import ConcurrencyPopUpStore from '../ConcurrencyPopUpStore';
import InputStore from '../InputStore';
import MenuStore from '../MenuStore';
import AsyncActionStore from '../AsyncActionStore';
import PolicyModalStore, { ModalMode } from './PolicyModalStore';
import FlagStore from '../FlagStore';

export const PRIORITY_BUTTONS = {
    UP: 'up',
    DOWN: 'down',
    TOP: 'top',
    BOTTOM: 'bottom',
};

export default class PolicyTableStore {
    static PropType = PropTypes.shape({
        items: MobxPropTypes.arrayOrObservableArray.isRequired,
        reorderedItems: MobxPropTypes.arrayOrObservableArray.isRequired,
        search: InputStore.PropType.isRequired,
        menu: MenuStore.PropType.isRequired,
        policyModal: PolicyModalStore.PropType.isRequired,
        priorityChangeModal: FlagStore.PropType.isRequired,
        cancelPriorityChangeConfirmationModal: FlagStore.PropType.isRequired,
        priorityChangeStatus: AsyncActionStore.PropType,
        listStatus: AsyncActionStore.PropType,

        load: PropTypes.func.isRequired,
        changePriorities: PropTypes.func.isRequired,
        openModal: PropTypes.func.isRequired,
        modalCompleteCb: PropTypes.func,
        setItems: PropTypes.func.isRequired,
        changeItemsOrder: PropTypes.func.isRequired,
        hasOrderChanged: PropTypes.func.isRequired,
        cancelReorder: PropTypes.func.isRequired,
        canMoveUp: PropTypes.func.isRequired,
        canMoveDown: PropTypes.func.isRequired,
        getFirstCanMoveItem: PropTypes.func.isRequired,
        getLastCanMoveItem: PropTypes.func.isRequired,

        concurrencyPopUpStore: ConcurrencyPopUpStore.PropType,
        apiList: PropTypes.func.isRequired,
        apiChangePriorities: PropTypes.func.isRequired,

        onLoadListeners: MobxPropTypes.arrayOrObservableArrayOf(PropTypes.func),
        addOnLoadListener: PropTypes.func.isRequired,
    });

    constructor(data = {}) {
        extendObservable(this, /** @class PolicyTableStore */{
            items: [],
            reorderedItems: [],
            search: new InputStore(),
            menu: new MenuStore(),
            priorityChangeModal: new FlagStore(false),
            cancelPriorityChangeConfirmationModal: new FlagStore(false),
            policyModal: new PolicyModalStore({
                noHeader: data.noHeader || false,
                preprocessHeaderValue: data.preprocessHeaderValue || null,
                noStatusField: data.noStatusField || false,
                sections: data.modalSections || {},
                [ModalMode.EDIT]: this.edit,
                [ModalMode.CREATE]: this.create,
                [ModalMode.DELETE]: this.delete,
                onComplete: this.onModalComplete,
                requireEditConfirmation: data.requireEditConfirmation || (() => false),
                preprocessBeforeSubmit: data.preprocessBeforeSubmit || null,
            }),

            apiList: () => Promise.resolve([]),
            apiChangePriorities: () => Promise.resolve([]),

            listStatus: new AsyncActionStore(),
            priorityChangeStatus: new AsyncActionStore(),

            onLoadListeners: [],
            addOnLoadListener: (fn) => {
                this.onLoadListeners.push(fn);
                return () => {
                    this.onLoadListeners = this.onLoadListeners.filter((listener) => listener !== fn);
                };
            },

            focusPriorityItemIndex: null,
            focusPriorityItemButtonType: null,
            concurrencyPopUpStore: null,
        }, data);

        reaction(
            () => this.concurrencyPopUpStore?.isOpen.getValue(),
            this.policyModal.actionSpinnerDisabled.setValue,
        );

        reaction(() => this.policyModal.modal.isOpen, (value) => {
            if (!value) {
                this.policyModal.setMode(null);
            }
        });
    }

    load = action(() => {
        if (!this.listStatus.isLoading) {
            this.listStatus.setLoading(this.apiList()
                .then((items) => {
                    this.onLoadListeners.map((h) => h(items));
                    this.setItems(items);
                }));
        }
        return this.listStatus.promise;
    });

    openModal = action((mode, item, cbFn = undefined) => {
        this.modalCompleteCb = cbFn;
        this.menu.close();
        this.policyModal.setMode(mode);
        if (item) {
            this.policyModal.setValue(item);
        } else {
            this.policyModal.resetToDefaults();
        }
        this.policyModal.modal.open();
    });

    onModalComplete = action((modalMode, item) => {
        if (modalMode === ModalMode.EDIT || modalMode === ModalMode.CREATE || modalMode === ModalMode.EDIT_CONFIRM) {
            this.search.reset();
        }

        if (this.modalCompleteCb) {
            this.modalCompleteCb(item);
        }
    });

    setItems = action((items) => {
        const newItems = items
            .map((item, itemIndex) => ({ ...item, fixedPriority: itemIndex + 1 }));
        this.items.replace(newItems);
        this.reorderedItems.replace(newItems);
    });

    changePriorities = action(() => {
        if (!this.priorityChangeStatus.isLoading) {
            this.priorityChangeStatus.setLoading(
                this.apiChangePriorities(this.reorderedItems
                    .filter((item) => !item.isDefault)
                    .map((item) => item.id)
                ).then(this.load)
            );
        }
        return this.priorityChangeStatus.promise;
    });

    hasOrderChanged = () => {
        const toHashStr = (arr) => arr.map(({ id }) => id).join();
        return toHashStr(this.items) !== toHashStr(this.reorderedItems);
    }

    changeItemsOrder = action((fromIndex, toIndex) => {
        const [item] = this.reorderedItems.splice(fromIndex, 1);
        this.reorderedItems.splice(toIndex, 0, item);
    });

    cancelReorder = action(() => this.reorderedItems.replace(this.items));

    canMoveUp = (itemIndex) => {
        if (this.reorderedItems.length === 0) {
            return false;
        }
        return itemIndex > 0 && !this.reorderedItems[itemIndex - 1].isDefault;
    };

    canMoveDown = (itemIndex) => {
        if (this.reorderedItems.length === 0) {
            return false;
        }
        return itemIndex < this.reorderedItems.length - 1 && !this.reorderedItems[itemIndex + 1].isDefault;
    };

    getFirstCanMoveItem = () => {
        if (this.reorderedItems.length === 0) {
            return -1;
        }
        return this.reorderedItems.findIndex((item) => !item.isDefault);
    };

    getLastCanMoveItem = () => {
        if (this.reorderedItems.length === 0) {
            return -1;
        }
        return this.reorderedItems.length -
            [...this.reorderedItems].reverse().findIndex((item) => !item.isDefault) - 1;
    };

    finishModalOp = () =>
        this.load().then(this.policyModal.modal.hide)

    handleConcurrencyCancel = (res) => () =>
        res(ConcurrencyPopUpStore.CONCURRENCY_MODAL_CANCEL_LABEL);

    create = (newItem) => {
        const priority = this.items.reduce((minPriority, item) => Math.min(item.priority, minPriority), 0) - 1;
        return this.concurrencyPopUpStore.apiCreate({ ...newItem, priority }).then(this.finishModalOp);
    }

    edit = (item) => new Promise((res, rej) => {
        this.concurrencyPopUpStore.update(
            item,
            (updatedItem) => {
                this.finishModalOp();
                res(updatedItem);
            },
            this.handleConcurrencyCancel(res)
        ).catch(rej);
    });

    delete = (item) => new Promise((res, rej) => {
        this.concurrencyPopUpStore.delete(
            item,
            () => {
                this.finishModalOp();
                res();
            },
            this.handleConcurrencyCancel(res)
        ).catch(rej);
    });

    setPriorityItemFocusButton = action((itemIndex, btnType) => {
        this.focusPriorityItemIndex = itemIndex;
        this.focusPriorityItemButtonType = btnType;
    });

    isPriorityButtonFocused = (itemIndex, btnType) => {
        return this.focusPriorityItemIndex === itemIndex && this.focusPriorityItemButtonType === btnType;
    };

    getOnPriorityButtonClickFn = (fromIndex, toIndex, btnType) => {
        return (event) => {
            if (event.currentTarget === document.activeElement) {
                if (btnType === PRIORITY_BUTTONS.UP && !this.canMoveUp(toIndex)) {
                    btnType = PRIORITY_BUTTONS.DOWN;
                }

                if (btnType === PRIORITY_BUTTONS.DOWN && !this.canMoveDown(toIndex)) {
                    btnType = PRIORITY_BUTTONS.UP;
                }

                if (btnType === PRIORITY_BUTTONS.TOP) {
                    btnType = PRIORITY_BUTTONS.BOTTOM;
                } else if (btnType === PRIORITY_BUTTONS.BOTTOM) {
                    btnType = PRIORITY_BUTTONS.TOP;
                }

                this.setPriorityItemFocusButton(toIndex, btnType);
            } else {
                this.setPriorityItemFocusButton(null, null);
            }

            this.changeItemsOrder(fromIndex, toIndex);
        };
    };

    getOnPriorityButtonBlurFn = (itemIndex, btnType) => {
        if (itemIndex === this.focusPriorityItemIndex && btnType === this.focusPriorityItemButtonType) {
            return () => {
                if (itemIndex === this.focusPriorityItemIndex && btnType === this.focusPriorityItemButtonType) {
                    this.setPriorityItemFocusButton(null, null);
                }
            };
        }
        return undefined;
    };

    makeCopy(item) {
        this.menu.close();

        const copyItem = {
            ...item,
            name: 'Copy of ' + item.name,
        };

        if (item.status) {
            copyItem.status = 'DISABLED';
        }

        return this.create(copyItem);
    }
}
